import type {
  EvaluationCriteriaEducationYear,
  EvaluationCriteriaSelectedItem,
  EvaluationCriteriaSubjectOption,
  EvaluationCriterion,
  Region,
} from 'app/types/evaluationCriteria';
import dataManagerService from 'core/chatbot/services/dataManagerService';
import { useOrganization } from 'core/hooks/useOrganization';
import * as coreEntities from 'core/store/index';
import type { TFunction } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logger } from 'shared/infrastructure/logger';
import {
  findDisciplineOption,
  getAllowedEducationYears,
  getDisciplineOptions,
  isEducationYearAllowed,
  normalizeSubjects,
  resolveSelectedDiscipline,
} from './evaluationCriteriaOptions';
import type { CatalogDiscipline, DisciplineOption } from './evaluationCriteriaOptions';

type LegacyValueItem = EvaluationCriteriaSelectedItem | string;

type UseSelectLearningObjectivesTanAIParams = {
  disciplineId?: string;
  educationLevel?: string;
  educationYear?: string;
  onChange: (next: EvaluationCriteriaSelectedItem[]) => void;
  placeholder?: string;
  value: LegacyValueItem[];
};

type RegionOption = { label: string; value: string };

type EducationYearValue = {
  educationLevel: string;
  educationYear: string;
  educationLevelLabel?: string;
  educationYearLabel?: string;
};

type UseSelectLearningObjectivesTanAIReturn = {
  t: TFunction;
  isOpen: boolean;
  isLoadingRegions: boolean;
  isLoadingCriteria: boolean;
  hasSearched: boolean;
  isSearchEnabled: boolean;
  isDisciplineDisabled: boolean;
  isEducationYearDisabled: boolean;
  regionOptions: RegionOption[];
  selectedRegionOption: RegionOption | null;
  disciplineOptions: DisciplineOption[];
  selectedDisciplineOption: DisciplineOption | null;
  disciplinePlaceholder: string;
  disciplineError: string;
  disciplineStatusMessage: string;
  allowedEducationYears: EvaluationCriteriaEducationYear[];
  criteria: EvaluationCriterion[];
  normalizedValue: EvaluationCriteriaSelectedItem[];
  pendingSelection: EvaluationCriteriaSelectedItem[];
  resolvedPlaceholder: string;
  educationYearValue: EducationYearValue;
  showMoreLabel: string;
  showLessLabel: string;
  handleOpenDialog: () => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  handleRegionChange: (option: RegionOption | null) => void;
  handleDisciplineChange: (option: Pick<DisciplineOption, 'value'> | null) => void;
  handleEducationYearChange: (data: Partial<EducationYearValue> | null | undefined) => void;
  handleSearch: () => Promise<void>;
  handleToggleById: (id: string) => void;
  handleRemoveTag: (id: string) => void;
  handleSave: () => void;
  handleCancel: () => void;
  isCriterionSelected: (id: string) => boolean;
};

const COUNTRY_CODE = 'ES';

const EMPTY_EDUCATION_YEAR: EducationYearValue = { educationLevel: '', educationYear: '' };

// Sin token es un estado esperado (sesión aún sin cargar o ya cerrada) que `defaultErrorHandler` ya calla: no es un bug
// y va como miga, no como evento de Sentry (`docs/sentry/niveles.md`, regla 1).
function logRequestError(message: string, error: unknown, regionCode: string): void {
  const isMissingAuthToken = typeof error === 'object' && error !== null && (error as { isMissingAuthToken?: unknown }).isMissingAuthToken === true;
  const log = isMissingAuthToken ? logger.info : logger.error;
  log(message, { error, tags: { region_code: regionCode } });
}

function useSelectLearningObjectivesTanAI({
  disciplineId,
  educationLevel,
  educationYear,
  onChange,
  placeholder,
  value,
}: UseSelectLearningObjectivesTanAIParams): UseSelectLearningObjectivesTanAIReturn {
  const { t } = useTranslation(['chatbot', 'common', 'actions']);
  const { client } = useOrganization();
  const dispatch = useDispatch();

  const disciplines: CatalogDiscipline[] = useSelector((state: unknown) => coreEntities.educationLevels.selectors.getDisciplines(state)) || [];

  // Backwards-compat: accept legacy `string[]` values and normalize them to objects.
  const normalizedValue: EvaluationCriteriaSelectedItem[] = useMemo(
    () => (value ?? []).map((item) => (typeof item === 'string' ? { id: item, description: item } : item)),
    [value],
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [regionCode, setRegionCode] = useState<string>('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [criteria, setCriteria] = useState<EvaluationCriterion[]>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState<boolean>(false);
  const [isLoadingCriteria, setIsLoadingCriteria] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [hasFetchedRegions, setHasFetchedRegions] = useState<boolean>(false);
  const [subjects, setSubjects] = useState<EvaluationCriteriaSubjectOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  const [hasOptionsError, setHasOptionsError] = useState<boolean>(false);
  // `null` mientras el usuario no ha elegido disciplina: entonces manda la preseleccionada en el formulario.
  const [chosenDisciplineId, setChosenDisciplineId] = useState<string | null>(null);
  const [educationYearValue, setEducationYearValue] = useState<EducationYearValue>({
    educationLevel: educationLevel ?? '',
    educationYear: educationYear ?? '',
  });
  // Pending selection inside the dialog. We commit it to the parent only on Save.
  const [pendingSelection, setPendingSelection] = useState<EvaluationCriteriaSelectedItem[]>(normalizedValue);

  // Load disciplines (and education levels) from Redux when they are missing
  useEffect(() => {
    if (!disciplines || disciplines.length === 0) {
      dispatch(coreEntities.educationLevels.actions.getEducationLevels({ client }));
    }
  }, [dispatch, client]);

  // Tracks the most recent search request so stale responses can be discarded.
  const searchRequestIdRef = useRef<number>(0);
  // Igual con las opciones: solo se aplica la respuesta de la última comunidad elegida.
  const optionsRequestIdRef = useRef<number>(0);

  // Las props del formulario pueden cambiar después de montar (carga asíncrona, AIQuizSection): vuelven a mandar sobre
  // la elección y descartan los resultados de los filtros anteriores. Se compara con el valor anterior para no tocar el
  // estado inicial en el primer montaje.
  const previousDisciplineIdRef = useRef<string | undefined>(disciplineId);
  const previousEducationYearPropsRef = useRef<string>(`${educationLevel ?? ''}|${educationYear ?? ''}`);

  useEffect(() => {
    if (previousDisciplineIdRef.current === disciplineId) return;
    previousDisciplineIdRef.current = disciplineId;
    setChosenDisciplineId(null);
    resetSearchResults();
  }, [disciplineId]);

  useEffect(() => {
    const nextProps = `${educationLevel ?? ''}|${educationYear ?? ''}`;
    if (previousEducationYearPropsRef.current === nextProps) return;
    previousEducationYearPropsRef.current = nextProps;
    resetSearchResults();
    setEducationYearValue((prev) => {
      const nextLevel = educationLevel ?? '';
      const nextYear = educationYear ?? '';
      if (prev.educationLevel === nextLevel && prev.educationYear === nextYear) {
        return prev;
      }
      return {
        ...prev,
        educationLevel: nextLevel,
        educationYear: nextYear,
      };
    });
  }, [educationLevel, educationYear]);

  // Keep pendingSelection in sync with the parent value whenever the dialog is closed.
  // When the dialog is open we don't overwrite pending changes that the user is editing.
  useEffect(() => {
    if (isOpen) return;
    setPendingSelection(normalizedValue);
  }, [normalizedValue, isOpen]);

  const resolvedPlaceholder = placeholder ?? t('chatbot:teacher_tools_select_evaluation_criteria_no_selection_placeholder');
  const showMoreLabel = t('chatbot:teacher_tools_select_evaluation_criteria_show_more');
  const showLessLabel = t('chatbot:teacher_tools_select_evaluation_criteria_show_less');

  // Fetch regions only the first time the dialog opens
  useEffect(() => {
    let isCancelled = false;

    async function fetchRegions(): Promise<void> {
      setIsLoadingRegions(true);
      try {
        const result = await dataManagerService.getRegions(COUNTRY_CODE);
        if (isCancelled) return;
        setRegions(Array.isArray(result) ? result : []);
        // Only mark as fetched on success so a failed request retries when the dialog reopens.
        setHasFetchedRegions(true);
      } catch (error) {
        console.warn('Error fetching evaluation criteria regions:', error);
        if (isCancelled) return;
        setRegions([]);
      } finally {
        if (!isCancelled) {
          setIsLoadingRegions(false);
        }
      }
    }

    if (isOpen && !hasFetchedRegions) {
      fetchRegions();
    }

    return () => {
      isCancelled = true;
    };
  }, [isOpen, hasFetchedRegions]);

  const regionOptions: RegionOption[] = regions.map((region) => ({
    label: region?.name,
    value: region?.code,
  }));

  const selectedRegionOption: RegionOption | null = regionOptions.find((option) => option.value === regionCode) ?? null;

  const disciplineOptions = getDisciplineOptions(subjects, disciplines);
  const selectedDisciplineId = resolveSelectedDiscipline({ currentDisciplineId: chosenDisciplineId, preselectedDisciplineId: disciplineId, subjects });
  // Sin etiqueta del catálogo no hay opción que mostrar: la disciplina sigue elegida, pero no se busca hasta que llegue.
  const selectedDisciplineOption = findDisciplineOption({ catalog: disciplines, disciplineId: selectedDisciplineId, options: disciplineOptions, subjects });
  const searchDisciplineId = selectedDisciplineOption?.value ?? '';

  const allowedEducationYears = getAllowedEducationYears(subjects, selectedDisciplineId);
  const isEducationYearValid = isEducationYearAllowed(allowedEducationYears, educationYearValue);

  const isDisciplineDisabled = !regionCode || isLoadingOptions;
  const isEducationYearDisabled = !selectedDisciplineOption;
  const isSearchEnabled = Boolean(regionCode && selectedDisciplineOption && isEducationYearValid);

  // Carga y comunidad sin asignaturas se anuncian por la región de estado, que no se ve: el placeholder las repite para
  // quien mira la pantalla. El error ya se ve y se anuncia bajo el select (`role="alert"`): el placeholder no lo repite.
  function getDisciplineStatusMessage(): string {
    if (isLoadingOptions) return t('chatbot:teacher_tools_select_evaluation_criteria_loading_subjects');
    if (regionCode && !hasOptionsError && subjects.length === 0) return t('chatbot:teacher_tools_select_evaluation_criteria_no_subjects');
    return '';
  }

  const disciplineStatusMessage = getDisciplineStatusMessage();
  const disciplineError = hasOptionsError ? t('chatbot:teacher_tools_select_evaluation_criteria_options_error') : '';
  const disciplinePlaceholder = disciplineStatusMessage || t('chatbot:teacher_tools_select_evaluation_criteria_discipline_placeholder');

  function handleOpenDialog(): void {
    // Reset pending selection to the committed value so unconfirmed changes are discarded.
    setPendingSelection(normalizedValue);
    setIsOpen(true);
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDialog();
    }
  }

  // Al cambiar un filtro se descarta también la búsqueda en vuelo: si no, su respuesta pintaría (y dejaría guardar)
  // criterios de los filtros anteriores, y Buscar seguiría deshabilitado hasta que llegara.
  function resetSearchResults(): void {
    searchRequestIdRef.current += 1;
    setIsLoadingCriteria(false);
    setHasSearched(false);
    setCriteria([]);
  }

  function keepEducationYearIfAllowed(allowed: EvaluationCriteriaEducationYear[]): void {
    setEducationYearValue((prev) => (isEducationYearAllowed(allowed, prev) ? prev : EMPTY_EDUCATION_YEAR));
  }

  // Al llegar las opciones, disciplina y nivel se conservan solo si la comunidad los tiene: se vacían en el estado y
  // no solo en pantalla, para que no reaparezcan al volver a una comunidad que sí los tenga. Depende solo de la
  // respuesta: el catálogo de disciplinas puede llegar después y solo pone la etiqueta.
  function keepValidSelection(nextSubjects: EvaluationCriteriaSubjectOption[]): void {
    const nextDisciplineId = resolveSelectedDiscipline({ currentDisciplineId: chosenDisciplineId, preselectedDisciplineId: disciplineId, subjects: nextSubjects });
    setChosenDisciplineId(nextDisciplineId);
    keepEducationYearIfAllowed(getAllowedEducationYears(nextSubjects, nextDisciplineId));
  }

  async function loadSubjectOptions(nextRegionCode: string): Promise<void> {
    optionsRequestIdRef.current += 1;
    const requestId = optionsRequestIdRef.current;
    setSubjects([]);
    setHasOptionsError(false);
    setIsLoadingOptions(Boolean(nextRegionCode));
    if (!nextRegionCode) return;

    try {
      const result = await dataManagerService.getEvaluationCriteriaOptions(nextRegionCode);
      if (requestId !== optionsRequestIdRef.current) return;
      const nextSubjects = normalizeSubjects(Array.isArray(result) ? result : []);
      setSubjects(nextSubjects);
      keepValidSelection(nextSubjects);
    } catch (error) {
      logRequestError('Error al cargar las asignaturas con criterios de evaluación', error, nextRegionCode);
      if (requestId !== optionsRequestIdRef.current) return;
      setHasOptionsError(true);
    } finally {
      if (requestId === optionsRequestIdRef.current) {
        setIsLoadingOptions(false);
      }
    }
  }

  // Elegir otra vez la misma comunidad también recarga: es como se reintenta tras un error.
  function handleRegionChange(option: RegionOption | null): void {
    const nextRegionCode = option?.value ?? '';
    setRegionCode(nextRegionCode);
    resetSearchResults();
    loadSubjectOptions(nextRegionCode);
  }

  function handleDisciplineChange(option: Pick<DisciplineOption, 'value'> | null): void {
    const nextDisciplineId = option?.value ?? '';
    setChosenDisciplineId(nextDisciplineId);
    keepEducationYearIfAllowed(getAllowedEducationYears(subjects, nextDisciplineId));
    resetSearchResults();
  }

  function handleEducationYearChange(data: Partial<EducationYearValue> | null | undefined): void {
    setEducationYearValue({
      educationLevel: data?.educationLevel ?? '',
      educationYear: data?.educationYear ?? '',
      educationLevelLabel: data?.educationLevelLabel,
      educationYearLabel: data?.educationYearLabel,
    });
    resetSearchResults();
  }

  const handleSearch = useCallback(async (): Promise<void> => {
    if (!isSearchEnabled) return;

    searchRequestIdRef.current += 1;
    const requestId = searchRequestIdRef.current;

    setIsLoadingCriteria(true);
    setHasSearched(true);

    try {
      const result = await dataManagerService.getEvaluationCriteria({
        disciplineId: searchDisciplineId,
        regionCode,
        educationLevel: educationYearValue?.educationLevel,
        educationYear: educationYearValue?.educationYear,
      });
      // Discard the response if a newer search has been triggered in the meantime.
      if (requestId !== searchRequestIdRef.current) return;
      setCriteria(Array.isArray(result) ? result : []);
    } catch (error) {
      logRequestError('Error al cargar los criterios de evaluación', error, regionCode);
      if (requestId !== searchRequestIdRef.current) return;
      setCriteria([]);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsLoadingCriteria(false);
      }
    }
  }, [isSearchEnabled, searchDisciplineId, regionCode, educationYearValue]);

  function isCriterionSelected(id: string): boolean {
    return pendingSelection.some((item) => item?.id === id);
  }

  function handleToggleById(id: string): void {
    const exists = isCriterionSelected(id);
    if (exists) {
      setPendingSelection((prev) => prev.filter((item) => item?.id !== id));
      return;
    }
    const criterion = criteria.find((c) => c?.id === id);
    if (!criterion) return;
    setPendingSelection((prev) => [...prev, { id: criterion.id, description: criterion.description }]);
  }

  function handleRemoveTag(id: string): void {
    // Removing a tag from outside the dialog is applied immediately.
    onChange(normalizedValue.filter((item) => item?.id !== id));
  }

  function handleSave(): void {
    onChange(pendingSelection);
    setIsOpen(false);
  }

  function handleCancel(): void {
    setPendingSelection(normalizedValue);
    setIsOpen(false);
  }

  return {
    t,
    isOpen,
    isLoadingRegions,
    isLoadingCriteria,
    hasSearched,
    isSearchEnabled,
    isDisciplineDisabled,
    isEducationYearDisabled,
    regionOptions,
    selectedRegionOption,
    disciplineOptions,
    selectedDisciplineOption,
    disciplinePlaceholder,
    disciplineError,
    disciplineStatusMessage,
    allowedEducationYears,
    criteria,
    normalizedValue,
    pendingSelection,
    resolvedPlaceholder,
    educationYearValue,
    showMoreLabel,
    showLessLabel,
    handleOpenDialog,
    handleInputKeyDown,
    handleRegionChange,
    handleDisciplineChange,
    handleEducationYearChange,
    handleSearch,
    handleToggleById,
    handleRemoveTag,
    handleSave,
    handleCancel,
    isCriterionSelected,
  };
}

export default useSelectLearningObjectivesTanAI;
